import { Button, Popover, Tag, Tooltip } from 'antd';
import CustomSelect from '../custom-select/custom-select';
import { orderBy } from 'lodash';
import { DistributePolicyConnectionItemRenderer } from '../label-renderer/label-renderer';
import CustomIcon, { IconType } from '../custom-icon/custom-icon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DistributePolicyConnection, DistributePolicyState } from '../../../dtos/push-policy.dto';
import { SystemConfiguration } from '../../../utils/system-configuration';
import { useStores } from '../../store';
import { SelectProps } from 'antd/lib/select';
import usePromiseFeedback from '../promise-feedback/usePromiseFeedback';
import { observer } from 'mobx-react-lite';
import InfoIcon from '@svg/info-circle.svg?react';
import './multi-select-connections.scss';
import { ServiceDto } from '../../../dtos/customer.dto';
import Loader from '../loader/loader';
import Info from '@svg/info.svg?react';

interface MultiSelectConnectionsProps {
    label?: string;
    sourceSid?: number;
    selectedConnections: number[];
    setSelectedConnections: (values: number[]) => void;
    defaultConnection?: ServiceDto;
    error?: string;
    disabled?: boolean;
    isScheduled?: boolean;
}

export const MultiSelectConnections: React.FC<MultiSelectConnectionsProps> = observer(({label, sourceSid, selectedConnections, setSelectedConnections,
                                                                                           defaultConnection, error, disabled, isScheduled}) => {
    type TagRender = SelectProps['tagRender'];

    const { customerStore, fwVersionStore } = useStores();
    const { maxBulkPolicyUpdate } = SystemConfiguration.getInstance();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [connections, setConnections] = useState<any[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [isLoadingConnections, setIsLoadingConnections] = useState(true);
    const [invalidConnectionNames, setInvalidConnectionNames] = useState<string[]>([]);
    const [validationMessage, setValidationMessage] = useState<string>('');

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const allSelectedConnections = useMemo(() => defaultConnection ? [defaultConnection.serviceId, ...selectedConnections] : selectedConnections,
        [selectedConnections]);
    const isMaxConnectionsReached = useMemo(() => allSelectedConnections.length >= maxBulkPolicyUpdate
        , [allSelectedConnections.length]);

    const loadConnections = () => {
        setConnections([]);
        setIsLoadingConnections(true);
        promiseFeedbackFactory({
            promise: fwVersionStore.getAvailableConnectionsForPushPolicy(customerStore.selectedCustomer?.customerId, sourceSid, !defaultConnection, isScheduled ?? false),
            notifObj: { show: false },
            successCallback: (data: { [index: number]: DistributePolicyConnection }) => {
                const mappedConnections = mapAndOrderConnections(data);
                handleInvalidSelectedConnections(connections, mappedConnections);
                const finalConnections = addDefaultConnection(mappedConnections);
                setConnections(finalConnections);
                setIsLoadingConnections(false);
            },
            errorCallback: () => setIsLoadingConnections(false)
        });
    };

    const mapAndOrderConnections = (data: { [index: number]: DistributePolicyConnection }) => {
        return orderBy(
            data,
            [
                conn => conn.distributePolicyState === DistributePolicyState.VALID,
                conn => conn.targetSidName.toLowerCase()
            ],
            ['desc', 'asc']
        ).map(conn => ({
            key: conn.targetSid,
            value: conn.targetSid,
            label: conn.targetSidName,
            data: conn
        }));
    };

    const handleInvalidSelectedConnections = (oldConnections: any[], newConnections: any[]) => {
        const invalidConnections = selectedConnections.filter(_ => {
            const oldConnection = oldConnections.find(oldConn=>oldConn.key === _);
            const newConnection = newConnections.find(newConn=>newConn.key === _);
            return oldConnection?.data.distributePolicyState === DistributePolicyState.VALID
                && newConnection?.data.distributePolicyState !== DistributePolicyState.VALID;
        });

        if (invalidConnections.length) {
            const names = (invalidConnections.map(invalidConnection => {
                return newConnections.find(_ => _.key === invalidConnection)?.label;
            }));
            setValidationMessage(`${names.length}/${selectedConnections.length} failed validation`);
            setInvalidConnectionNames(names);
            setSelectedConnections(selectedConnections.filter(s => !invalidConnections.includes(s)));
        }

    };

    const addDefaultConnection = (connections: any[]) => {
        if (!defaultConnection) return connections;

        const defaultConn = {
            key: defaultConnection.serviceId,
            value: defaultConnection.serviceId,
            label: defaultConnection.serviceName || defaultConnection.adiCircuitId,
            data: convertServiceDtoToPushPolicyConnection(defaultConnection)
        };

        return [defaultConn, ...connections];
    };

    const convertServiceDtoToPushPolicyConnection = (service: ServiceDto): DistributePolicyConnection => {
        return {
            targetSid: service.serviceId,
            targetSidName: service.serviceName || service.adiCircuitId,
            targetSidLevel: service.serviceLevel,
            distributePolicyState: DistributePolicyState.VALID
        };
    };

    useEffect(() => {
        loadConnections();
        if (isScheduled) {
            setInvalidConnectionNames([]);
            setValidationMessage('');
        }
    }, [isScheduled]);

    useEffect(() => {
        if (isDropdownOpen) {
            setIsPopoverOpen(false);
        }
    }, [isDropdownOpen]);

    const isDefaultConnection = (sid: number) =>
        defaultConnection && sid === defaultConnection?.serviceId;

    const removeConnection = (connectionId: number) =>
      setSelectedConnections(selectedConnections.filter(id => id !== connectionId));

    const popoverContentRow = (con: any) => (
        <div className={`info-row ${isDefaultConnection(con.value) ? 'default' : ''}`} key={con.key}>
            <div>{con.label}</div>
            {!disabled && !isDefaultConnection(con.value) && <Button className={'button-link'} onClick={() => removeConnection(con.value)}>X</Button>}
        </div>
    );

    const popoverContent = useMemo(() =>
            connections.filter(con => allSelectedConnections.includes(con.value)).map(popoverContentRow)
        , [connections, allSelectedConnections, disabled]);

    const tagRender: TagRender = useCallback((props: any) => {
        const {label, closable, onClose} = props;
        const data = (label.props?.data as DistributePolicyConnection) ??
            (defaultConnection && label === defaultConnection.serviceId ? convertServiceDtoToPushPolicyConnection(defaultConnection) : undefined);

        return data && (
            <Tag closable={data.targetSid === defaultConnection?.serviceId ? false : closable}
                 aria-disabled={data.targetSid === defaultConnection?.serviceId} onClose={onClose}>
                {data.targetSidName}
            </Tag>
        );
    }, [allSelectedConnections]);

    const isOptionDisabled = useCallback((data: DistributePolicyConnection) => {
        if (isDefaultConnection(data.targetSid)) {
            return true;
        }

        if (selectedConnections.includes(data.targetSid)) {
            return false;
        }
        return isMaxConnectionsReached || data.distributePolicyState !== DistributePolicyState.VALID;
    }, [selectedConnections, isMaxConnectionsReached]);

    const openPopover = () => {
        if (selectedConnections.length > 0 && !isDropdownOpen) {
            setIsPopoverOpen(true);
        } else {
            setIsPopoverOpen(false);
        }
    };

    const maxConnectionsError = <span className={'max-connections'}>{maxBulkPolicyUpdate} connections limit is reached</span>;

    return (<div className={'multi-select-connections'}>
        {!!label && <div className={'policy-action-modal-label'}>
            <div className={'policy-action-modal-description'}>
                {label}
            </div>
            {isMaxConnectionsReached && maxConnectionsError}
        </div>}
        {validationMessage && <span className={'validation-message'}>
                    {validationMessage}
            <Tooltip
                title={`Due to validation failure, Apply policy won’t be executed for the following connections: ${invalidConnectionNames.join(', ')}`}>
                <Info/>
            </Tooltip>
        </span>}
        <Popover content={popoverContent} placement={'right'}
                 overlayClassName={'push-policy-max-tag-popover'}
                 trigger={'hover'}
                 open={isPopoverOpen && !isLoadingConnections}
                 onOpenChange={(visible) => {
                     if (!visible) setIsPopoverOpen(false);
                 }}
        >
            <>
                <CustomSelect placeholder={'Select Connections'}
                              className={['policy-manager-select-connection', 'dropdown-custom',
                                  `${isMaxConnectionsReached ? 'max-connections-dropdown' : ''}`,
                                  `${error ? 'has-error' : ''}`].join(' ')}
                              onChange={(values: number[]) => setSelectedConnections(values.filter(_ => _ !== defaultConnection?.serviceId))}
                              disabled={!customerStore.selectedCustomer || disabled}
                              options={connections}
                              value={allSelectedConnections}
                              emptyMessage={'No connection found'}
                              selectProps={{
                                  showSearch: false,
                                  onDropdownVisibleChange: setIsDropdownOpen,
                                  mode: 'multiple', maxTagCount: 'responsive',
                                  showArrow: true, tagRender: tagRender,
                                  loading: isLoadingConnections,
                                  onMouseEnter: openPopover,
                              }}
                              isOptionDisabled={isOptionDisabled}
                              LabelRenderer={DistributePolicyConnectionItemRenderer}/>
                {selectedConnections.length > 0 && isLoadingConnections && <span className={'connections-re-render'}><Loader type={'component'} /></span>}
                {isMaxConnectionsReached && !label && maxConnectionsError}
                {!!error && <span className="error"><CustomIcon type={IconType.RED_ERROR} size={16}/>{error}</span>}
            </>
        </Popover>
        <div className={'alert-component light'}>
            <div className={'alert-icon'}><InfoIcon /></div>
            <div className={'alert-message'}>{defaultConnection? 'Apply' : 'Copy'} policy overrides the draft policy at selected connections.</div>
        </div>
        {promiseContextHolder}
    </div>);
});